import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import {
  RosterMember,
  MarketplaceItem,
  Proposal,
  ArtistProfile,
  UserRole,
  Artist,
  Notification,
  ApprovalStatus,
  ProposalStatus,
  SupportTicket,
} from '../types';
import {
  MOCK_ROSTER,
  MOCK_MARKETPLACE_ITEMS,
  MOCK_PROPOSALS,
  MOCK_SUPPORT_TICKETS,
} from '../mockData';
import { useLogger } from './LogContext';
import { LogLevel, LogCategory } from '@/types/logs';

export interface DataContextType {
  users: RosterMember[];
  marketItems: MarketplaceItem[];
  proposals: Proposal[];
  leads: ArtistProfile[];
  notifications: Notification[];
  supportTickets: SupportTicket[];
  addUser: (user: ArtistProfile) => void;
  updateUser: (user: Partial<RosterMember>) => void;
  addMarketItem: (item: MarketplaceItem) => void;
  addLead: (artist: Artist) => boolean;
  updateLeadStatus: (leadId: string, status: ArtistProfile['leadStatus']) => void;
  removeLead: (leadId: string) => void;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  markNotificationsAsRead: (userId: string) => void;
  purgeMockData: () => void;
  isDemoMode: boolean;
  setDemoMode: (isDemo: boolean) => void;
  demoModeAvailable: boolean;
  setDemoModeAvailable: (available: boolean) => void;
  stats: {
    totalMembers: number;
    activeGigs: number;
    totalTransactions: string;
  };
  // Auth & User Lookup
  findUserByEmail: (email: string) => RosterMember | undefined;
  findUserByWallet: (address: string) => RosterMember | undefined;
  getUserById: (id: string) => RosterMember | undefined;
}

export const DataContext = createContext<DataContextType | undefined>(
  undefined
);

export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const transformRosterMemberToArtistProfile = (
  rosterMember: RosterMember,
  fallbackProfile: ArtistProfile
): ArtistProfile => {
  if (!rosterMember) {
      return fallbackProfile; 
  }
  return {
    ...fallbackProfile,
    id: rosterMember.id,
    name: rosterMember.name,
    role: rosterMember.role,
    avatar: rosterMember.avatar,
    location: rosterMember.location,
    verified: rosterMember.verified,
    walletAddress: rosterMember.walletAddress,
    email: rosterMember.subscriberOnly?.email || '',
    bio: `A member of the KalaKrut community since ${new Date().getFullYear()}`,
    description: `A member of the KalaKrut community since ${new Date().getFullYear()}`,
    coverImage: `https://picsum.photos/seed/${rosterMember.id}/1200/400`,
    genres: fallbackProfile.genres,
    pressKit: fallbackProfile.pressKit,
    stats: fallbackProfile.stats,
    xp: fallbackProfile.xp,
    level: fallbackProfile.level,
    leadStatus: 'Converted',
    status: ApprovalStatus.APPROVED
  };
};

const tagMocks = <T extends object>(data: T[]): (T & { isMock: boolean })[] =>
  data.map((item) => ({ ...item, isMock: true }));

const SYSTEM_ADMIN: RosterMember = {
  id: 'sys_admin_live',
  name: 'System Admin (Live)',
  role: UserRole.SYSTEM_ADMIN_LIVE,
  avatar:
    'https://ui-avatars.com/api/?name=System+Admin&background=0D8ABC&color=fff',
  location: 'Server Room',
  verified: true,
  rating: 5.0,
  assets: { ips: [], contents: [], events: [], products: [], services: [], equipment: [], instruments: [], tickets: [] },
  subscriberOnly: { email: 'admin@kalakrut.io', phone: 'N/A', agentContact: 'System' },
  isMock: false,
  status: ApprovalStatus.APPROVED,
};

const SUPER_ADMIN: RosterMember = {
  id: 'super_admin_bhoomin',
  name: 'Super Admin',
  role: UserRole.ADMIN,
  avatar:
    'https://ui-avatars.com/api/?name=Super+Admin&background=8b5cf6&color=fff',
  location: 'Global HQ',
  verified: true,
  rating: 5.0,
  assets: { ips: [], contents: [], events: [], products: [], services: [], equipment: [], instruments: [], tickets: [] },
  subscriberOnly: { email: 'bhoominpandya@gmail.com', phone: '+1 (555) 000-SUPER', agentContact: 'Direct' },
  isMock: false,
  password: 'Creatkala!2',
  walletAddress: '0x82255198336C1c9c82f7Fbe89bb9706A78A71783',
  status: ApprovalStatus.APPROVED,
};

export const DataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { addLog } = useLogger();
  const [allUsers, setAllUsers] = useState<RosterMember[]>([]);
  const [allLeads, setAllLeads] = useState<ArtistProfile[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [allMarketItems, setAllMarketItems] = useState<MarketplaceItem[]>([]);
  const [allProposals, setAllProposals] = useState<Proposal[]>([]);
  const [supportTickets, setSupportTickets] = useState<SupportTicket[]>([]);
  const [isDemoMode, setIsDemoMode] = useState(true);
  const [demoModeAvailable, setDemoModeAvailable] = useState(() => {
    const saved = localStorage.getItem('kk_demo_available');
    return saved !== null ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    if (isDemoMode) {
      addLog('Operating in Demo Mode', LogLevel.INFO, LogCategory.SYSTEM);
      // Load data from localStorage or mocks when in demo mode
      const savedUsers = localStorage.getItem('kk_users');
      if (savedUsers) {
        setAllUsers(JSON.parse(savedUsers));
      } else {
        setAllUsers([...tagMocks(MOCK_ROSTER), SYSTEM_ADMIN, SUPER_ADMIN]);
      }

      const savedLeads = localStorage.getItem('kk_leads');
      if (savedLeads) setAllLeads(JSON.parse(savedLeads));

      const savedNotifications = localStorage.getItem('kk_notifications');
      if (savedNotifications) setNotifications(JSON.parse(savedNotifications));

      const savedMarketItems = localStorage.getItem('kk_market');
      if (savedMarketItems) {
        setAllMarketItems(JSON.parse(savedMarketItems));
      } else {
        setAllMarketItems(tagMocks(MOCK_MARKETPLACE_ITEMS));
      }

      const savedProposals = localStorage.getItem('kk_proposals');
      if (savedProposals) {
        setAllProposals(JSON.parse(savedProposals));
      } else {
        setAllProposals(tagMocks(MOCK_PROPOSALS));
      }

      const savedSupportTickets = localStorage.getItem('kk_support_tickets');
      if (savedSupportTickets) {
        setSupportTickets(JSON.parse(savedSupportTickets));
      } else {
        setSupportTickets(MOCK_SUPPORT_TICKETS);
      }

    } else {
      addLog('Operating in Live Mode', LogLevel.INFO, LogCategory.SYSTEM);
      const fetchData = async () => {
        // Fetch Users
        try {
          addLog('Fetching users...', LogLevel.INFO, LogCategory.DATA);
          const usersResponse = await fetch('/api/users');
          if (!usersResponse.ok) throw new Error(`Server responded with ${usersResponse.status}`);
          const usersData = await usersResponse.json();
          setAllUsers(usersData);
          addLog('Users fetched successfully', LogLevel.INFO, LogCategory.DATA, { userCount: usersData.length });
        } catch (error) {
          addLog('Error fetching users from server', LogLevel.ERROR, LogCategory.DATA, { error: error instanceof Error ? error.message : String(error) });
        }
        
        // Fetch Leads
        try {
          addLog('Fetching leads...', LogLevel.INFO, LogCategory.DATA);
          const leadsResponse = await fetch('/api/leads');
          if (!leadsResponse.ok) throw new Error(`Server responded with ${leadsResponse.status}`);
          const leadsData = await leadsResponse.json();
          setAllLeads(leadsData);
          addLog('Leads fetched successfully', LogLevel.INFO, LogCategory.DATA, { leadCount: leadsData.length });
        } catch (error) {
          addLog('Error fetching leads from server', LogLevel.ERROR, LogCategory.DATA, { error: error instanceof Error ? error.message : String(error) });
        }

        // Fetch Market Items
        try {
          addLog('Fetching market items...', LogLevel.INFO, LogCategory.DATA);
          const marketResponse = await fetch('/api/market');
          if (!marketResponse.ok) throw new Error(`Server responded with ${marketResponse.status}`);
          const marketData = await marketResponse.json();
          setAllMarketItems(marketData);
          addLog('Market items fetched successfully', LogLevel.INFO, LogCategory.DATA, { itemCount: marketData.length });
        } catch (error) {
          addLog('Error fetching market items. Falling back to mock data.', LogLevel.WARN, LogCategory.DATA, { error: error instanceof Error ? error.message : String(error) });
          setAllMarketItems(tagMocks(MOCK_MARKETPLACE_ITEMS));
        }

        // Fetch Proposals
        try {
          addLog('Fetching proposals...', LogLevel.INFO, LogCategory.DATA);
          const proposalsResponse = await fetch('/api/proposals');
          if (!proposalsResponse.ok) throw new Error(`Server responded with ${proposalsResponse.status}`);
          const proposalsData = await proposalsResponse.json();
          setAllProposals(proposalsData);
          addLog('Proposals fetched successfully', LogLevel.INFO, LogCategory.DATA, { proposalCount: proposalsData.length });
        } catch (error) {
          addLog('Error fetching proposals. Falling back to mock data.', LogLevel.WARN, LogCategory.DATA, { error: error instanceof Error ? error.message : String(error) });
          setAllProposals(tagMocks(MOCK_PROPOSALS));
        }

        // Fetch Support Tickets
        try {
          addLog('Fetching support tickets...', LogLevel.INFO, LogCategory.DATA);
          const supportResponse = await fetch('/api/support');
          if (!supportResponse.ok) throw new Error(`Server responded with ${supportResponse.status}`);
          const supportData = await supportResponse.json();
          setSupportTickets(supportData);
          addLog('Support tickets fetched successfully', LogLevel.INFO, LogCategory.DATA, { ticketCount: supportData.length });
        } catch (error) {
          addLog('Error fetching support tickets. Falling back to mock data.', LogLevel.WARN, LogCategory.DATA, { error: error instanceof Error ? error.message : String(error) });
          setSupportTickets(MOCK_SUPPORT_TICKETS);
        }
      };
      fetchData();
    }
  }, [isDemoMode, addLog]);


  useEffect(() => { 
    if(isDemoMode) {
      localStorage.setItem('kk_users', JSON.stringify(allUsers)); 
    }
  }, [allUsers, isDemoMode]);
  
  useEffect(() => { 
    if(isDemoMode) {
      localStorage.setItem('kk_leads', JSON.stringify(allLeads)); 
    }
  }, [allLeads, isDemoMode]);

  useEffect(() => { 
    if(isDemoMode) {
      localStorage.setItem('kk_notifications', JSON.stringify(notifications)); 
    }
  }, [notifications, isDemoMode]);

  useEffect(() => { 
    if(isDemoMode) {
      localStorage.setItem('kk_market', JSON.stringify(allMarketItems)); 
    }
  }, [allMarketItems, isDemoMode]);

  useEffect(() => { 
    if(isDemoMode) {
      localStorage.setItem('kk_proposals', JSON.stringify(allProposals)); 
    }
  }, [allProposals, isDemoMode]);

  useEffect(() => { 
    if(isDemoMode) {
      localStorage.setItem('kk_support_tickets', JSON.stringify(supportTickets)); 
    }
  }, [supportTickets, isDemoMode]);

  useEffect(() => {
    localStorage.setItem('kk_demo_available', JSON.stringify(demoModeAvailable));
    if (!demoModeAvailable) setIsDemoMode(false);
  }, [demoModeAvailable]);

  const addUser = (profile: ArtistProfile) => {
    const newRosterMember: RosterMember = {
      id: profile.id,
      name: profile.name,
      role: profile.role,
      avatar: profile.avatar || 'https://picsum.photos/seed/new_user/200',
      location: profile.location,
      verified: false,
      rating: 0,
      walletAddress: profile.walletAddress,
      assets: { ips: [], contents: [], events: [], products: [], services: [], equipment: [], instruments: [], tickets: [] },
      subscriberOnly: { email: profile.email || 'hidden', phone: 'Hidden', agentContact: 'Direct' },
      isMock: false,
      password: profile.password,
      status: ApprovalStatus.PENDING,
    };
    setAllUsers((prev) => [newRosterMember, ...prev.filter((p) => p.id !== newRosterMember.id)]);
  };

  const updateUser = (updates: Partial<RosterMember>) => {
    setAllUsers((prev) => prev.map((user) => (user.id === updates.id ? { ...user, ...updates } : user)));
  };
  
  const addNotification = (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    const newNotification: Notification = {
      ...notification,
      id: `notif-${Date.now()}`,
      timestamp: new Date().toISOString(),
      read: false,
    };
    setNotifications(prev => [newNotification, ...prev]);
  };

  const markNotificationsAsRead = (userId: string) => {
    setNotifications(prev => prev.map(n => (n.userId === userId ? { ...n, read: true } : n)));
  };

  const addMarketItem = (item: MarketplaceItem) => {
    setAllMarketItems((prev) => [{ ...item, isMock: false }, ...prev]);
  };

  const addLead = (artist: Artist): boolean => {
    let wasAdded = false;
    setAllLeads((prev) => {
      if (prev.some((l) => l.id === artist.id)) {
        wasAdded = false;
        return prev;
      }
      const newLead: ArtistProfile = {
        id: artist.id,
        name: artist.name,
        bio: artist.bio,
        description: artist.bio,
        email: '',
        location: '',
        status: ApprovalStatus.PENDING,
        leadStatus: 'New',
        role: UserRole.ARTIST,
        avatar: 'https://ui-avatars.com/api/?name=' + artist.name.replace(/ /g, '+'),
        coverImage: '',
        genres: [],
        verified: false,
        pressKit: { photos: [], topTracks: [], techRiderUrl: '', socials: [] },
        stats: { gigsCompleted: 0, activeGigs: 0, rating: 0, responseTime: 'N/A' },
        xp: 0,
        level: 1,
      };
      wasAdded = true;
      return [newLead, ...prev];
    });
    return wasAdded;
  };
  
  const updateLeadStatus = (leadId: string, status: ArtistProfile['leadStatus']) => {
    setAllLeads(prev => prev.map(lead => lead.id === leadId ? { ...lead, leadStatus: status } : lead));
  };

  const removeLead = (leadId: string) => {
    setAllLeads(prev => prev.filter(lead => lead.id !== leadId));
  };

  const purgeMockData = () => {
    if (window.confirm("WARNING: This will delete all 'Working Example' data. This action cannot be undone. Are you sure?")) {
      addLog('Purging mock data', LogLevel.WARN, LogCategory.SYSTEM);
      setAllUsers((prev) => prev.filter((u) => !u.isMock));
      setAllMarketItems((prev) => prev.filter((i) => !i.isMock));
      setAllProposals((prev) => prev.filter((p) => !p.isMock));
    }
  };

  const findUserByEmail = (email: string) => allUsers.find((u) => u.subscriberOnly?.email?.toLowerCase() === email.toLowerCase());
  const findUserByWallet = (address: string) => allUsers.find((u) => u.walletAddress?.toLowerCase() === address.toLowerCase());
  const getUserById = (id: string) => allUsers.find((u) => u.id === id);

  const visibleUsers = allUsers.filter(u => u.status === ApprovalStatus.APPROVED && !u.isMock);
  const visibleProposals = isDemoMode ? allProposals : allProposals.filter((p) => !p.isMock);
  
  const visibleMarket = allMarketItems.map(item => {
    const seller = getUserById(item.sellerId);
    return {
      ...item,
      title: item.title, // Keep original title
      seller: seller ? {
        name: seller.name,
        avatar: seller.avatar,
        verified: seller.verified,
      } : {
        name: "Unknown Seller",
        avatar: "",
        verified: false,
      }
    };
  });

  const stats = {
    totalMembers: visibleUsers.length,
    activeGigs: visibleProposals.filter((p) => p.status === ProposalStatus.ACTIVE).length,
    totalTransactions: isDemoMode ? (12 + visibleUsers.filter((u) => !u.isMock).length).toString() : '0',
  };

  return (
    <DataContext.Provider
      value={{
        users: visibleUsers,
        marketItems: visibleMarket,
        proposals: visibleProposals,
        leads: allLeads,
        notifications,
        supportTickets,
        addUser,
        updateUser,
        addMarketItem,
        addLead,
        updateLeadStatus,
        removeLead,
        addNotification,
        markNotificationsAsRead,
        purgeMockData,
        isDemoMode,
        setDemoMode: setIsDemoMode,
        demoModeAvailable,
        setDemoModeAvailable,
        stats,
        findUserByEmail,
        findUserByWallet,
        getUserById,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};
